import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

/**
 * Enhanced SEO Component for S.K. Woollens
 * Manages document title, meta tags, canonical, hreflang alternates (with x-default),
 * OpenGraph, Twitter Cards, and dynamic Schema.org JSON-LD structured data.
 */
const SEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = "https://skwoolens.trivanahospitality.com/sk-woollens-logo-512.png",
  ogType = "website",
  noindex = false,
  schema = null,
}) => {
  const { language } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    // 1. Document Title
    if (title) {
      document.title = title;
    }

    // Helper: Upsert Meta Tag
    const setMeta = (name, attr, value) => {
      if (!value) return;
      let el = document.querySelector(`meta[${name}="${attr}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(name, attr);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    // 2. Meta Description & Keywords
    if (description) setMeta("name", "description", description);
    if (keywords) setMeta("name", "keywords", keywords);

    // 3. Robots Meta Directive
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    // 4. Open Graph Tags
    if (title) setMeta("property", "og:title", title);
    if (description) setMeta("property", "og:description", description);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:image", ogImage);

    // 5. Twitter Card Tags
    setMeta("property", "twitter:card", "summary_large_image");
    if (title) setMeta("property", "twitter:title", title);
    if (description) setMeta("property", "twitter:description", description);
    setMeta("property", "twitter:image", ogImage);

    // 6. Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }

    const cleanPath = location.pathname.replace(/\/+$/, "") || "";
    const computedCanonical = canonicalUrl || `https://skwoolens.trivanahospitality.com${cleanPath || "/en"}`;
    canonicalLink.setAttribute("href", computedCanonical);
    setMeta("property", "og:url", computedCanonical);

    // 7. Hreflang Alternates & x-default
    const alternates = document.querySelectorAll('link[rel="alternate"]');
    alternates.forEach((el) => el.remove());

    const supportedLangs = [
      { code: "en", pathPrefix: "/en" },
      { code: "en-au", pathPrefix: "/en-au" },
      { code: "en-nz", pathPrefix: "/en-nz" },
      { code: "ar", pathPrefix: "/ar" },
      { code: "zh-cn", pathPrefix: "/zh-cn" },
    ];

    // Extract subpath without the leading language code
    const pathParts = location.pathname.split("/").filter(Boolean);
    const hasLangPrefix = pathParts.length > 0 && supportedLangs.some((l) => l.code === pathParts[0].toLowerCase());
    const subSegments = hasLangPrefix ? pathParts.slice(1) : pathParts;
    const subPath = subSegments.length > 0 ? `/${subSegments.join("/")}` : "";

    // x-default fallback (pointing to the English variant)
    const xDefaultLink = document.createElement("link");
    xDefaultLink.setAttribute("rel", "alternate");
    xDefaultLink.setAttribute("hreflang", "x-default");
    xDefaultLink.setAttribute("href", `https://skwoolens.trivanahospitality.com/en${subPath}`);
    document.head.appendChild(xDefaultLink);

    // Specific language alternates
    supportedLangs.forEach(({ code, pathPrefix }) => {
      const altLink = document.createElement("link");
      altLink.setAttribute("rel", "alternate");
      altLink.setAttribute("hreflang", code);
      altLink.setAttribute("href", `https://skwoolens.trivanahospitality.com${pathPrefix}${subPath}`);
      document.head.appendChild(altLink);
    });

    // 8. Dynamic Structured Data (Schema.org JSON-LD)
    let dynamicSchemaScript = document.getElementById("dynamic-page-schema");
    if (schema) {
      if (!dynamicSchemaScript) {
        dynamicSchemaScript = document.createElement("script");
        dynamicSchemaScript.id = "dynamic-page-schema";
        dynamicSchemaScript.type = "application/ld+json";
        document.head.appendChild(dynamicSchemaScript);
      }
      dynamicSchemaScript.textContent = JSON.stringify(schema, null, 2);
    } else if (dynamicSchemaScript) {
      dynamicSchemaScript.remove();
    }

    return () => {
      // Clean up dynamic schema when unmounting if appropriate
      const script = document.getElementById("dynamic-page-schema");
      if (script) script.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, noindex, schema, location.pathname, language]);

  return null;
};

export default SEO;
